"use client";

import { useCallback } from "react";
import { SearchParameters } from "@/types/dto/search-filters/search-parameters";
import {
  searchParametersToFilterValues,
  searchParametersToPriceRange,
  filterValuesToSearchParameters
} from "@/factories/search-filters/search-parameters-factory";

interface UseFilterActionsProps {
  currentSearchParams: Partial<SearchParameters>;
  onFiltersChange?: (filters: Partial<SearchParameters>) => void;
  onClearAll?: () => void;
  onFilterInteraction?: (filterType: string, filterValue: string | string[], action: "add" | "remove" | "clear") => void;
}

const useFilterActions = ({ currentSearchParams, onFiltersChange, onClearAll, onFilterInteraction }: UseFilterActionsProps) => {

  // Convert search parameters using factory functions
  const filterValues = searchParametersToFilterValues(currentSearchParams);
  const priceRange = searchParametersToPriceRange(currentSearchParams);
  // Filter change handler
  const handleFilterChange = useCallback((data: Record<string, string>, title?: string) => {
    const [fieldName, value] = Object.entries(data)[0];
    const field = title !== null && title !== undefined && title !== "" ? title : fieldName;
    // Always get fresh values from current search params
    const currentFilterValues = searchParametersToFilterValues(currentSearchParams);
    const currentPriceRange = searchParametersToPriceRange(currentSearchParams);
    const currentValues = currentFilterValues[fieldName] || [];
    const isRemoving = currentValues.includes(value);
    const newValues = currentValues.includes(value)
      ? currentValues.filter(v => v !== value) // Remove if already selected
      : [...currentValues, value]; // Add if not selected
    if (onFilterInteraction) {
      onFilterInteraction(field, value, isRemoving ? "remove" : "add");
    }
    // Update filter values and convert back to SearchParameters using factory
    const updatedFilterValues = { ...currentFilterValues, [fieldName]: newValues };

    const updatedParams = filterValuesToSearchParameters(updatedFilterValues, currentPriceRange);

    if (onFiltersChange) {
      onFiltersChange(updatedParams);
    }
  }, [currentSearchParams, onFiltersChange, onFilterInteraction]);

  // Clear field handler
  const handleClearField = useCallback((fieldName: string) => {
    if (onFilterInteraction) {
      const currentFilterValues = searchParametersToFilterValues(currentSearchParams);
      const currentValues = currentFilterValues[fieldName] || [];
      onFilterInteraction(fieldName, currentValues, "clear");
    }
    // Always get fresh values from current search params
    const currentFilterValues = searchParametersToFilterValues(currentSearchParams);
    const currentPriceRange = searchParametersToPriceRange(currentSearchParams);
    const updatedFilterValues = { ...currentFilterValues, [fieldName]: [] };
    const updatedParams = filterValuesToSearchParameters(updatedFilterValues, currentPriceRange);

    if (onFiltersChange) {
      onFiltersChange(updatedParams);
    }
  }, [currentSearchParams, onFiltersChange, onFilterInteraction]);

  // Price range change handler
  const handlePriceRangeChange = useCallback((data: Record<string, string>) => {
    if (onFilterInteraction) {
      const [key, value] = Object.entries(data)[0];
      onFilterInteraction("Price Range", `${key}:${value}`, "add");
    }
    // Always get fresh values from current search params
    const currentFilterValues = searchParametersToFilterValues(currentSearchParams);
    const currentPriceRange = searchParametersToPriceRange(currentSearchParams);
    const updatedPriceRange = { ...currentPriceRange, ...data };
    const updatedParams = filterValuesToSearchParameters(currentFilterValues, updatedPriceRange);

    if (onFiltersChange) {
      onFiltersChange(updatedParams);
    }
  }, [currentSearchParams, onFiltersChange, onFilterInteraction]);

  // Clear all filters handler
  const handleClearAll = useCallback(() => {
    if (onFilterInteraction) {
      onFilterInteraction("Clear All Button", "clear_all", "clear");
    }
    if (onClearAll) {
      onClearAll();
    }
  }, [onClearAll, onFilterInteraction]);

  return {
    filterValues,
    priceRange,
    handleFilterChange,
    handleClearField,
    handlePriceRangeChange,
    handleClearAll
  };
};

export default useFilterActions;